import { useEffect, useRef } from "react";

import { TYPEOF_UNDEFINED } from "../../../../constants";

/**
 * Generic event listener hook. Registers and cleans up with the same handler reference.
 * @param {string} eventName - DOM event name (e.g. "resize", "mousedown")
 * @param {Function} handler - Callback invoked when the event fires
 * @param {EventTarget|null|undefined} element - Target (defaults to window when available)
 * @param {boolean|AddEventListenerOptions} [options] - addEventListener options
 */
export const useEventListener = (eventName, handler, element, options) => {
  const savedHandler = useRef(handler);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const target =
      element ??
      (typeof window !== TYPEOF_UNDEFINED ? window : undefined);

    if (!target?.addEventListener || !eventName) {
      return undefined;
    }

    const eventListener = (event) => savedHandler.current?.(event);
    target.addEventListener(eventName, eventListener, options);

    return () => {
      target.removeEventListener(eventName, eventListener, options);
    };
  }, [eventName, element, options]);
};
